/**
 * The router (architecture §3.2; ui-ux §3.3–§3.4). Hash routes are the source of truth; `history.state` carries
 * `{ marg: 1, idx, overlay?, params? }` so back/forward are told apart and overlays close before routes change.
 *
 * Three presentation layers:
 * - **base** — one routed page / tab root / immersive screen (`kind` ≠ sheet);
 * - **sheet** — a routed sheet page (`#/life/day`, `#/arena/ladder`, `#/arena/result/:id`, `#duel=`) over its base;
 *   a deep link mounts the base first (the nearest ancestor route, or the tab root);
 * - **overlays** — route-less screens opened with `goto(id)` (`combat/advice`, `meta/item`, `explore/summary`, …):
 *   each pushes a history entry, so Android back / browser back / Esc close the top overlay first.
 *
 * Rules: a screen is unmounted only after the next one's `enter()` resolves; navigation during a transition is queued
 * (latest wins); `goto` to the current screen with equal params is a no-op; `back()` asks the top screen's `onBack()`
 * first. Transitions follow ui-ux §9.2 (tab crossfade 150 ms, push/pop slide 280 ms, sheet 240/200 ms; reduced motion
 * → ≤ 150 ms crossfade; min → ≤ 80 ms fade).
 */
import { Scope, type Router, type Screen, type ScreenContext, type ScreenDef, type ScreenId, type ScreenParams, type Viewport } from '../shared';
import { buildHash, findScreen, hashDepth, parentHash, redirectHash, resolveHash } from './routes';

export type MotionLevel = 'full' | 'reduced' | 'min';
export type Layer = 'base' | 'sheet' | 'overlay';

export interface Mounted {
  def: ScreenDef;
  params: ScreenParams;
  /** Route params only (equality for no-op checks). */
  routeKey: string;
  screen: Screen;
  scope: Scope;
  /** The container in its layer. */
  el: HTMLElement;
  layer: Layer;
  /** The hash this entry was mounted for (null = overlay or a route-less debug screen). */
  hash: string | null;
}

export interface RouterDeps {
  defs: ScreenDef[];
  baseLayer: HTMLElement;
  sheetLayer: HTMLElement;
  makeContext(def: ScreenDef, params: ScreenParams, scope: Scope): ScreenContext;
  motion(): MotionLevel;
  /** Called after every change of the mounted stack (shell chrome, title, renderer scene, bus). */
  onChange(top: Mounted, base: Mounted, from: ScreenId | null): void;
  /** Error report (`__marg.errors()`). */
  onError(message: string, err?: unknown): void;
  /** Search params of the page (feature params like `?fight=demo&auto=1` reach `parseParams`). */
  search(): URLSearchParams;
  /** The hash used when a route is unknown. */
  fallbackHash(): string;
  /** Strings for the error placeholder. */
  t(key: string, params?: Record<string, string | number>): string;
  sound?(id: 'ui_page' | 'ui_sheet'): void;
}

interface HistState { marg: 1; idx: number; overlay?: ScreenId; params?: ScreenParams }
type Target =
  | { kind: 'hash'; hash: string; transition?: 'page' | 'fade' | 'none' }
  | { kind: 'overlay'; def: ScreenDef; params: ScreenParams }
  | { kind: 'sync'; hash: string; overlay: { id: ScreenId; params: ScreenParams } | null };

const ENTER_TIMEOUT_MS = 2000;
const isSheetKind = (d: ScreenDef): boolean => d.kind === 'sheet' || d.kind === 'sheetPage';
const keyOf = (id: string, p: ScreenParams): string =>
  `${id}?${Object.keys(p).filter((k) => p[k] !== undefined).sort().map((k) => `${k}=${String(p[k])}`).join('&')}`;

function withTimeout<T>(p: Promise<T> | void, ms: number): Promise<void> {
  if (!p || typeof (p as Promise<T>).then !== 'function') return Promise.resolve();
  return Promise.race([(p as Promise<T>).then(() => undefined), new Promise<void>((r) => setTimeout(r, ms))]);
}

const finished = (a: Animation | null): Promise<void> => (a ? a.finished.then(() => undefined, () => undefined) : Promise.resolve());

export class AppRouter implements Router {
  private stack: Mounted[] = [];
  private idx = 0;
  private curState: HistState = { marg: 1, idx: 0 };
  private curUrl = '';
  private routerBack = false;
  private pumping = false;
  private next: { target: Target; resolvers: Array<() => void> } | null = null;
  private animating = 0;
  private readonly scroll = new Map<string, number>();
  private started = false;

  constructor(private readonly deps: RouterDeps) {}

  // ── Router contract ──────────────────────────────────────────────────────────────────────────────────────────────

  get current(): { id: ScreenId; params: ScreenParams } | null {
    const top = this.top;
    return top ? { id: top.def.id, params: top.params } : null;
  }

  list(): ScreenDef[] { return this.deps.defs.slice(); }

  goto(idOrHash: ScreenId | string, params: ScreenParams = {}, opts: { replace?: boolean; transition?: 'page' | 'fade' | 'none' } = {}): Promise<void> {
    if (idOrHash.startsWith('#')) return this.gotoHash(idOrHash, opts);
    const def = findScreen(this.deps.defs, idOrHash);
    if (!def) {
      this.deps.onError(`router: unknown screen "${idOrHash}"`);
      return Promise.resolve();
    }
    if (def.route) {
      let hash: string;
      try { hash = buildHash(def.route, params); } catch (e) {
        this.deps.onError(`router: ${(e as Error).message} (${def.id})`);
        return Promise.resolve();
      }
      return this.gotoHash(hash, opts);
    }
    if (def.kind === 'debug' || this.stack.length === 0) {
      // Route-less debug screens stay on the query form and replace the base.
      return this.enqueue({ kind: 'overlay', def, params: { ...params, __base: 1 } });
    }
    const top = this.top;
    if (top && top.def.id === def.id && top.routeKey === keyOf(def.id, params)) return Promise.resolve();
    this.idx++;
    this.curState = { marg: 1, idx: this.idx, overlay: def.id, params };
    history.pushState(this.curState, '', location.href);
    this.curUrl = location.href;
    return this.enqueue({ kind: 'overlay', def, params });
  }

  back(): void {
    const top = this.top;
    if (!top || this.routerBack) return;              // a back is already in flight (double Esc must not leave the app)
    if (top.screen.onBack?.()) return;
    if (top.layer === 'overlay' || (this.idx > 0)) {
      this.routerBack = true;
      history.back();
      return;
    }
    // Opened on a deep link (no in-app history): go to the parent route instead of leaving the app.
    const base = this.stack[0];
    const parent = top.layer === 'sheet' && base?.hash ? base.hash : (top.hash ? parentHash(this.deps.defs, top.hash) : null);
    if (parent && parent !== top.hash) void this.gotoHash(parent, { replace: true });
  }

  // ── app API ──────────────────────────────────────────────────────────────────────────────────────────────────────

  /** Mounts the initial route and starts listening to history. */
  start(initialHash: string): Promise<void> {
    if (this.started) return Promise.resolve();
    this.started = true;
    window.addEventListener('popstate', this.onPop);
    document.addEventListener('click', this.onClick);
    this.idx = 0;
    this.curState = { marg: 1, idx: 0 };
    const url = `${location.pathname}${location.search}${initialHash.startsWith('#') ? initialHash : ''}`;
    history.replaceState(this.curState, '', url);
    this.curUrl = location.href;
    return this.enqueue({ kind: 'hash', hash: initialHash || this.deps.fallbackHash() });
  }

  /** Starts on a route-less screen (debug screens: `?screen=animlab`). */
  startOn(def: ScreenDef): Promise<void> {
    if (this.started) return Promise.resolve();
    this.started = true;
    window.addEventListener('popstate', this.onPop);
    document.addEventListener('click', this.onClick);
    history.replaceState(this.curState, '', location.href);
    this.curUrl = location.href;
    return this.enqueue({ kind: 'overlay', def, params: { __base: 1 } });
  }

  get top(): Mounted | null { return this.stack[this.stack.length - 1] ?? null; }
  get base(): Mounted | null { return this.stack[0] ?? null; }
  /** Every mounted entry, base first. */
  mounted(): readonly Mounted[] { return this.stack; }

  /** True when no navigation or transition is in flight and every mounted screen reports idle. */
  isIdle(): boolean {
    if (this.pumping || this.next || this.animating > 0) return false;
    return this.stack.every((m) => m.screen.isIdle?.() !== false);
  }

  /** Re-creates every mounted screen in place (device-lost recovery, `__marg.setSeed`, quality change). */
  remountAll(reason: string): void {
    for (const m of this.stack) {
      try { m.screen.dispose(); } catch (e) { this.deps.onError(`router: dispose ${m.def.id} threw during ${reason}`, e); }
      m.scope.dispose();
      const scope = new Scope();
      m.scope = scope;
      m.screen = this.create(m.def, m.params, scope);
      const host = m.layer === 'base' ? m.el : (m.el.querySelector('.mc-route-sheet__body') as HTMLElement | null) ?? m.el;
      host.replaceChildren(m.screen.root);
      void m.screen.enter?.(null);
    }
    const top = this.top;
    if (top && this.base) this.deps.onChange(top, this.base, top.def.id);
  }

  /** Viewport change → every mounted screen. */
  resize(vp: Viewport): void { for (const m of this.stack) m.screen.resize?.(vp); }

  // ── internals ────────────────────────────────────────────────────────────────────────────────────────────────────

  private gotoHash(raw: string, opts: { replace?: boolean; transition?: 'page' | 'fade' | 'none' }): Promise<void> {
    const hash = redirectHash(raw);
    const top = this.top;
    const hit = resolveHash(this.deps.defs, hash);
    if (hit && top && top.def.id === hit.def.id && top.routeKey === keyOf(hit.def.id, hit.params) && top.layer !== 'overlay') return Promise.resolve();
    const url = `${location.pathname}${location.search}${hash}`;
    if (opts.replace) {
      this.curState = { marg: 1, idx: this.idx };
      history.replaceState(this.curState, '', url);
    } else {
      this.idx++;
      this.curState = { marg: 1, idx: this.idx };
      history.pushState(this.curState, '', url);
    }
    this.curUrl = location.href;
    return this.enqueue({ kind: 'hash', hash, transition: opts.transition });
  }

  private readonly onClick = (e: MouseEvent): void => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target instanceof Element ? e.target.closest('a[href]') : null) as HTMLAnchorElement | null;
    if (!a || a.target === '_blank') return;
    const href = a.getAttribute('href') ?? '';
    if (!href.startsWith('#') || href === '#') return;
    e.preventDefault();
    void this.goto(href);
  };

  private readonly onPop = (e: PopStateEvent): void => {
    const st = (e.state && (e.state as HistState).marg === 1 ? e.state : null) as HistState | null;
    const newIdx = st ? st.idx : this.idx + 1;
    const isBack = newIdx < this.idx;
    const routerBack = this.routerBack;
    this.routerBack = false;
    if (isBack && !routerBack) {
      const top = this.top;
      if (top?.screen.onBack?.()) {
        // The screen handled back (a pause menu, «Бросить правки?»): restore the entry we were on.
        history.pushState(this.curState, '', this.curUrl);
        return;
      }
    }
    if (!st) {
      this.curState = { marg: 1, idx: newIdx };
      history.replaceState(this.curState, '', location.href);
    } else {
      this.curState = st;
    }
    this.idx = newIdx;
    this.curUrl = location.href;
    const overlay = st?.overlay ? { id: st.overlay, params: st.params ?? {} } : null;
    void this.enqueue({ kind: 'sync', hash: location.hash || this.deps.fallbackHash(), overlay });
  };

  private enqueue(target: Target): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.next) { this.next.target = target; this.next.resolvers.push(resolve); }
      else this.next = { target, resolvers: [resolve] };
      void this.pump();
    });
  }

  private async pump(): Promise<void> {
    if (this.pumping) return;
    this.pumping = true;
    try {
      while (this.next) {
        const job = this.next;
        this.next = null;
        try { await this.apply(job.target); } catch (e) { this.deps.onError(`router: navigation failed: ${(e as Error).message}`, e); }
        for (const r of job.resolvers) r();
      }
    } finally {
      this.pumping = false;
    }
  }

  private async apply(target: Target): Promise<void> {
    if (target.kind === 'overlay') {
      if (target.params.__base) {
        const { __base: _drop, ...params } = target.params;
        void _drop;
        await this.closeAbove(0);
        await this.setBase(target.def, params, null, 'fade');
        return;
      }
      await this.openOverlay(target.def, target.params);
      return;
    }
    if (target.kind === 'sync') {
      // Close overlays above the target overlay (or all of them), then reconcile the route.
      const keepIdx = target.overlay ? this.stack.findIndex((m) => m.layer === 'overlay' && m.def.id === target.overlay!.id) : -1;
      const routeTop = this.stack.reduce((n, m, i) => (m.layer !== 'overlay' ? i : n), 0);
      await this.closeAbove(keepIdx >= 0 ? keepIdx : routeTop);
      await this.applyHash(target.hash, undefined, keepIdx >= 0);
      if (target.overlay && keepIdx < 0) {
        const def = findScreen(this.deps.defs, target.overlay.id);
        if (def) await this.openOverlay(def, target.overlay.params);
      }
      return;
    }
    await this.closeAbove(this.stack.reduce((n, m, i) => (m.layer !== 'overlay' ? i : n), 0));
    await this.applyHash(target.hash, target.transition, false);
  }

  /** Reconciles base + routed sheet with a hash. */
  private async applyHash(rawHash: string, transition: 'page' | 'fade' | 'none' | undefined, keepOverlays: boolean): Promise<void> {
    const hash = redirectHash(rawHash);
    if (hash !== rawHash) history.replaceState(this.curState, '', `${location.pathname}${location.search}${hash}`);
    const hit = resolveHash(this.deps.defs, hash);
    if (!hit) {
      const fb = this.deps.fallbackHash();
      this.deps.onError(`router: no screen for ${hash}; showing ${fb}`);
      history.replaceState(this.curState, '', `${location.pathname}${location.search}${fb}`);
      if (fb !== hash) await this.applyHash(fb, 'none', keepOverlays);
      return;
    }
    const params = this.mergeParams(hit.params);
    if (isSheetKind(hit.def)) {
      const base = this.stack[0];
      if (!base || base.def.tab !== hit.def.tab || base.layer !== 'base') {
        const ph = parentHash(this.deps.defs, hash) ?? this.tabRootHash(hit.def);
        const ph2 = ph ? resolveHash(this.deps.defs, ph) : null;
        if (ph && ph2) await this.setBase(ph2.def, this.mergeParams(ph2.params), ph, 'fade');
      }
      const sheetIdx = this.stack.findIndex((m) => m.layer === 'sheet');
      const existing = sheetIdx >= 0 ? this.stack[sheetIdx] : null;
      if (existing && existing.routeKey === keyOf(hit.def.id, hit.params)) return;
      if (existing) await this.closeAbove(sheetIdx - 1);
      await this.openSheet(hit.def, params, hash, 'sheet');
      return;
    }
    if (!keepOverlays) await this.closeAbove(0);
    else {
      const sheetIdx = this.stack.findIndex((m) => m.layer === 'sheet');
      if (sheetIdx >= 0) await this.closeAbove(sheetIdx - 1);
    }
    const base = this.stack[0];
    if (base && base.def.id === hit.def.id && base.routeKey === keyOf(hit.def.id, hit.params)) return;
    await this.setBase(hit.def, params, hash, transition);
  }

  private tabRootHash(def: ScreenDef): string | null {
    if (!def.tab) return null;
    const root = this.deps.defs.find((d) => d.tab === def.tab && d.kind === 'tabRoot' && d.route);
    if (!root?.route) return null;
    try { return buildHash(root.route, {}); } catch { return null; }
  }

  /** Feature params: page query (`?fight=demo&auto=1`) < hash query < route params. */
  private mergeParams(routeParams: ScreenParams): ScreenParams {
    const out: ScreenParams = {};
    this.deps.search().forEach((v, k) => { out[k] = v; });
    return { ...out, ...routeParams };
  }

  private create(def: ScreenDef, params: ScreenParams, scope: Scope): Screen {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, String(v));
    try {
      const parsed = def.parseParams(q);
      return def.create(this.deps.makeContext(def, parsed, scope), parsed);
    } catch (e) {
      this.deps.onError(`screen ${def.id}: create failed: ${(e as Error).message}`, e);
      return errorScreen(def, this.deps.t);
    }
  }

  private makeMounted(def: ScreenDef, params: ScreenParams, hash: string | null, layer: Layer): Mounted {
    const scope = new Scope();
    const screen = this.create(def, params, scope);
    const el = document.createElement('div');
    el.className = `mc-screen mc-screen--${layer}`;
    el.dataset.screenId = def.id;
    if (def.kind) el.dataset.kind = def.kind;
    return { def, params, routeKey: keyOf(def.id, stripQuery(params, this.deps.search())), screen, scope, el, layer, hash };
  }

  private transitionFor(from: Mounted | null, to: ScreenDef, hash: string | null, requested?: 'page' | 'fade' | 'none'): 'none' | 'fade' | 'push' | 'pop' {
    if (!from) return 'none';
    if (requested === 'none') return 'none';
    const motion = this.deps.motion();
    if (motion !== 'full') return 'fade';
    if (requested === 'fade') return 'fade';
    const immersive = (k?: string): boolean => k === 'immersive' || k === 'stage' || k === 'debug';
    if (from.def.tab !== to.tab || immersive(from.def.kind) || immersive(to.kind) || !from.hash || !hash) return 'fade';
    const a = hashDepth(from.hash), b = hashDepth(hash);
    if (requested === 'page') return b >= a ? 'push' : 'pop';
    return b > a ? 'push' : b < a ? 'pop' : 'fade';
  }

  private durations(): { fade: number; page: number; sheetIn: number; sheetOut: number } {
    const m = this.deps.motion();
    if (m === 'min') return { fade: 80, page: 80, sheetIn: 80, sheetOut: 80 };
    if (m === 'reduced') return { fade: 150, page: 150, sheetIn: 120, sheetOut: 120 };
    return { fade: 150, page: 280, sheetIn: 240, sheetOut: 200 };
  }

  private async setBase(def: ScreenDef, params: ScreenParams, hash: string | null, requested?: 'page' | 'fade' | 'none'): Promise<void> {
    const old = this.stack[0] ?? null;
    const kind = this.transitionFor(old, def, hash, requested);
    const m = this.makeMounted(def, params, hash, 'base');
    m.el.appendChild(m.screen.root);
    const layer = this.deps.baseLayer;
    if (old && kind === 'pop') layer.insertBefore(m.el, old.el); else layer.appendChild(m.el);
    if (old?.hash) this.scroll.set(old.hash, old.el.scrollTop);
    const restore = hash ? this.scroll.get(hash) : undefined;
    if (restore !== undefined) m.el.scrollTop = restore;
    const from = old?.def.id ?? null;
    // The new screen becomes the stack base immediately (update/render reach it during its enter transition).
    const rest = this.stack.slice(1);
    this.stack = [m, ...rest];
    this.deps.onChange(this.top!, m, from);
    if (kind !== 'none') this.deps.sound?.('ui_page');
    this.animating++;
    try {
      const d = this.durations();
      const ease = 'cubic-bezier(.2,.8,.2,1)';
      const anims: Array<Promise<void>> = [];
      if (old) void old.screen.exit?.(def.id);
      if (kind === 'fade') {
        anims.push(finished(m.el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: d.fade, easing: 'linear' })));
        if (old) anims.push(finished(old.el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: d.fade, easing: 'linear', fill: 'forwards' })));
      } else if (kind === 'push') {
        m.el.classList.add('is-pushing');
        anims.push(finished(m.el.animate([{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }], { duration: d.page, easing: ease })));
        if (old) anims.push(finished(old.el.animate([{ transform: 'translateX(0)', opacity: 1 }, { transform: 'translateX(-24%)', opacity: 0.5 }], { duration: d.page, easing: ease, fill: 'forwards' })));
      } else if (kind === 'pop') {
        if (old) {
          old.el.classList.add('is-pushing');
          anims.push(finished(old.el.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }], { duration: d.page, easing: ease, fill: 'forwards' })));
        }
        anims.push(finished(m.el.animate([{ transform: 'translateX(-24%)', opacity: 0.5 }, { transform: 'translateX(0)', opacity: 1 }], { duration: d.page, easing: ease })));
      }
      await withTimeout(m.screen.enter?.(from), ENTER_TIMEOUT_MS);
      await Promise.all(anims);
      m.el.classList.remove('is-pushing');
    } finally {
      this.animating--;
    }
    if (old) this.dispose(old);
  }

  private async openSheet(def: ScreenDef, params: ScreenParams, hash: string | null, layer: 'sheet' | 'overlay'): Promise<void> {
    const m = this.makeMounted(def, params, hash, layer);
    const full = def.kind === 'overlay' || def.kind === 'immersive';
    m.el.classList.add(full ? 'mc-screen--full' : 'mc-screen--sheet');
    const scrim = document.createElement('div');
    scrim.className = 'mc-route-scrim';
    scrim.addEventListener('click', () => this.back());
    const panel = document.createElement('div');
    panel.className = full ? 'mc-overlay-panel' : 'mc-route-sheet';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    if (!full) {
      const handle = document.createElement('div');
      handle.className = 'mc-route-sheet__handle';
      handle.setAttribute('aria-hidden', 'true');
      panel.appendChild(handle);
    }
    const body = document.createElement('div');
    body.className = 'mc-route-sheet__body';
    body.appendChild(m.screen.root);
    panel.appendChild(body);
    m.el.append(scrim, panel);
    this.deps.sheetLayer.appendChild(m.el);
    const from = this.top?.def.id ?? null;
    this.stack.push(m);
    this.deps.onChange(m, this.stack[0], from);
    this.deps.sound?.('ui_sheet');
    this.animating++;
    try {
      const d = this.durations();
      const motion = this.deps.motion();
      const side = !full && window.matchMedia('(min-width: 1024px) and (min-height: 521px)').matches;
      const anims: Array<Promise<void>> = [finished(scrim.animate([{ opacity: 0 }, { opacity: 1 }], { duration: d.sheetIn }))];
      if (motion !== 'full') anims.push(finished(panel.animate([{ opacity: 0 }, { opacity: 1 }], { duration: d.sheetIn })));
      else if (full) anims.push(finished(panel.animate([{ opacity: 0, transform: 'scale(.96)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 200, easing: 'cubic-bezier(.2,.8,.2,1)' })));
      else anims.push(finished(panel.animate([{ transform: side ? 'translateX(100%)' : 'translateY(100%)' }, { transform: 'none' }], { duration: d.sheetIn, easing: 'cubic-bezier(.2,.8,.2,1)' })));
      await withTimeout(m.screen.enter?.(from), ENTER_TIMEOUT_MS);
      await Promise.all(anims);
    } finally {
      this.animating--;
    }
  }

  private openOverlay(def: ScreenDef, params: ScreenParams): Promise<void> {
    return this.openSheet(def, params, null, 'overlay');
  }

  /** Closes (animated) every entry above index `keep`. */
  private async closeAbove(keep: number): Promise<void> {
    while (this.stack.length - 1 > keep && this.stack.length > 1) {
      const m = this.stack.pop()!;
      const top = this.top!;
      this.deps.onChange(top, this.stack[0], m.def.id);
      void m.screen.exit?.(top.def.id);
      this.animating++;
      try {
        const d = this.durations();
        const panel = m.el.querySelector('.mc-route-sheet, .mc-overlay-panel') as HTMLElement | null;
        const scrim = m.el.querySelector('.mc-route-scrim') as HTMLElement | null;
        const side = panel?.classList.contains('mc-route-sheet') && window.matchMedia('(min-width: 1024px) and (min-height: 521px)').matches;
        const motion = this.deps.motion();
        const anims: Array<Promise<void>> = [];
        if (scrim) anims.push(finished(scrim.animate([{ opacity: 1 }, { opacity: 0 }], { duration: d.sheetOut, fill: 'forwards' })));
        if (panel) {
          const kf = motion !== 'full' || panel.classList.contains('mc-overlay-panel')
            ? [{ opacity: 1 }, { opacity: 0 }]
            : [{ transform: 'none' }, { transform: side ? 'translateX(100%)' : 'translateY(100%)' }];
          anims.push(finished(panel.animate(kf, { duration: d.sheetOut, easing: 'cubic-bezier(.55,0,1,.45)', fill: 'forwards' })));
        }
        await Promise.all(anims);
      } finally {
        this.animating--;
      }
      this.dispose(m);
    }
  }

  private dispose(m: Mounted): void {
    try { m.screen.dispose(); } catch (e) { this.deps.onError(`screen ${m.def.id}: dispose threw`, e); }
    m.scope.dispose();
    m.el.remove();
  }
}

/** Route params only: drops values that merely echo the page query (`?debug=1` must not make every goto a change). */
function stripQuery(params: ScreenParams, search: URLSearchParams): ScreenParams {
  const out: ScreenParams = {};
  for (const [k, v] of Object.entries(params)) if (search.get(k) !== String(v)) out[k] = v;
  return out;
}

/** The placeholder shown when a screen's `create()` throws (the error is in `__marg.errors()`). */
function errorScreen(def: ScreenDef, t: RouterDeps['t']): Screen {
  const root = document.createElement('section');
  root.className = 'mc-screen-error';
  root.setAttribute('role', 'alert');
  const h = document.createElement('h1');
  h.textContent = t('app.error.screen.title');
  const p = document.createElement('p');
  p.textContent = t('app.error.screen.body');
  const code = document.createElement('code');
  code.textContent = def.id;
  root.append(h, p, code);
  return { root, dispose: () => root.remove(), isIdle: () => true, debugState: () => ({ error: true, id: def.id }) };
}
